import { useState, useRef, useCallback, useEffect, useMemo } from "react";

/**
 * usePanZoom
 *
 * Encapsulates the logic for computing cellSize, scale, offset,
 * and handling wheel zoom events.
 */
export function usePanZoom({ mapMetadata, stageWidth, stageHeight }) {
  const cellSize = useMemo(
    () => (mapMetadata ? stageWidth / mapMetadata.width : 1),
    [mapMetadata, stageWidth]
  );

  const computedMinScale = useMemo(() => {
    if (!mapMetadata) return 1;
    const naturalMapHeight = (stageWidth * mapMetadata.height) / mapMetadata.width;
    const fitScale =
      naturalMapHeight > stageHeight ? stageHeight / naturalMapHeight : 1;
    return Math.min(1, fitScale);
  }, [mapMetadata, stageWidth, stageHeight]);

  const initialScale = computedMinScale;

  const initialOffset = useMemo(() => {
    if (!mapMetadata) return { x: 0, y: 0 };
    return {
      x: (stageWidth * (1 - initialScale)) / 2,
      y:
        (stageHeight -
          (stageWidth * mapMetadata.height * initialScale) /
            mapMetadata.width) /
        2,
    };
  }, [mapMetadata, stageWidth, stageHeight, initialScale]);

  const [scale, setScale] = useState(initialScale);
  const [offset, setOffset] = useState(initialOffset);
  const scaleRef = useRef(scale);
  const offsetRef = useRef(offset);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  const clampOffsetFinal = useCallback(
    (offset, currentScale) => {
      const cellSize = stageWidth / mapMetadata.width;
      const mapWidth = stageWidth * currentScale;
      const mapHeight = cellSize * mapMetadata.height * currentScale;
      let minX, maxX, minY, maxY;
      if (mapWidth <= stageWidth) {
        minX = maxX = (stageWidth - mapWidth) / 2;
      } else {
        minX = stageWidth - mapWidth;
        maxX = 0;
      }
      if (mapHeight <= stageHeight) {
        minY = maxY = (stageHeight - mapHeight) / 2;
      } else {
        minY = stageHeight - mapHeight;
        maxY = 0;
      }
      return {
        x: Math.min(maxX, Math.max(offset.x, minX)),
        y: Math.min(maxY, Math.max(offset.y, minY)),
      };
    },
    [stageWidth, stageHeight, mapMetadata]
  );

  useEffect(() => {
    if (!mapMetadata) return;
    const clampedScale = Math.max(scaleRef.current || 1, computedMinScale);
    if (Math.abs(clampedScale - scaleRef.current) > 0.0001) {
      scaleRef.current = clampedScale;
      setScale(clampedScale);
    }
    setOffset((prev) => {
      const next = clampOffsetFinal(prev, clampedScale);
      if (next.x === prev.x && next.y === prev.y) {
        return prev;
      }
      return next;
    });
  }, [mapMetadata, computedMinScale, clampOffsetFinal]);

  const zoomAtPoint = useCallback(
    (screenX, screenY, targetScale) => {
      const prevScale = scaleRef.current;
      const prevOffset = offsetRef.current;
      const nextScale = Math.min(Math.max(targetScale, computedMinScale), 14);
      const worldX = (screenX - prevOffset.x) / prevScale;
      const worldY = (screenY - prevOffset.y) / prevScale;
      const newOffset = {
        x: screenX - worldX * nextScale,
        y: screenY - worldY * nextScale,
      };
      setScale(nextScale);
      setOffset(clampOffsetFinal(newOffset, nextScale));
    },
    [computedMinScale, clampOffsetFinal]
  );

  const handleWheel = useCallback(
    (e) => {
      let mousePos;
      if (e.data && e.data.global) {
        mousePos = e.data.global;
      } else if (
        e.clientX !== undefined &&
        e.clientY !== undefined &&
        e.target &&
        e.target.getBoundingClientRect
      ) {
        const rect = e.target.getBoundingClientRect();
        mousePos = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      } else {
        return;
      }
      const delta = -(
        (e.originalEvent && e.originalEvent.deltaY) ||
        e.deltaY ||
        0
      );
      const zoomFactor = delta > 0 ? 1.1 : 0.9;
      zoomAtPoint(mousePos.x, mousePos.y, scaleRef.current * zoomFactor);
    },
    [zoomAtPoint]
  );

  const setOffsetClamped = useCallback(
    (next) => {
      const resolved =
        typeof next === "function" ? next(offsetRef.current) : next;
      setOffset(clampOffsetFinal(resolved, scaleRef.current));
    },
    [clampOffsetFinal]
  );

  const panBy = useCallback(
    (dx, dy) => {
      setOffset((prev) =>
        clampOffsetFinal(
          { x: prev.x + dx, y: prev.y + dy },
          scaleRef.current
        )
      );
    },
    [clampOffsetFinal]
  );

  const getCellCoordinates = useCallback(
    (screenX, screenY) => {
      const adjustedX = (screenX - offset.x) / scale;
      const adjustedY = (screenY - offset.y) / scale;
      return {
        x: Math.floor(adjustedX / cellSize),
        y: Math.floor(adjustedY / cellSize),
      };
    },
    [offset, scale, cellSize]
  );

  return {
    cellSize,
    scale,
    offset,
    setOffset,
    setScale,
    setOffsetClamped,
    panBy,
    handleWheel,
    zoomAtPoint,
    getCellCoordinates,
    computedMinScale,
  };
}
